import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiGrid, FiList, FiFileText, FiTruck, FiMapPin, FiColumns } from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';
import { reportesService } from '../../services';
import { useProveedores } from '../../hooks/useProveedores/useProveedores';

const Proveedores = () => {
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState('grid');
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Usar el hook personalizado para proveedores
  const {
    proveedores,
    isLoading,
    error,
    searchQuery,
    searchProveedores,
    getProveedoresStats,
    clearError
  } = useProveedores();


  const handleAddNew = () => {
    navigate('/system/proveedores/nuevo');
  };

  const handleDownload = () => {
    setShowDownloadModal(true);
  };

  const handleDownloadReport = async ({ startDate, endDate, format }) => {
    setIsDownloading(true);

    try {
      // Llamar al servicio de reportes
      const params = {
        format: format, // csv o pdf
        date_from: startDate,
        date_to: endDate,
        is_active: true // Solo proveedores activos
      };

      const result = await reportesService.exportarProveedores(params);

      if (result.success) {
        // Determinar el tipo MIME según el formato
        const mimeTypes = {
          csv: 'text/csv',
          pdf: 'application/pdf',
          excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        };

        // Determinar la extensión del archivo
        const extensions = {
          csv: 'csv',
          pdf: 'pdf',
          excel: 'xlsx'
        };

        // Crear blob y descargar
        const blob = new Blob([result.data], {
          type: mimeTypes[format] || 'application/octet-stream'
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        // Generar nombre de archivo apropiado
        const fileName = startDate && endDate
          ? `proveedores_${startDate}_${endDate}.${extensions[format]}`
          : `proveedores_todos_${new Date().toISOString().split('T')[0]}.${extensions[format]}`;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);

        // Cerrar modal y mostrar mensaje de éxito
        setShowDownloadModal(false);
        alert(`Reporte descargado exitosamente en formato ${format.toUpperCase()}`);
      } else {
        throw new Error(result.message || 'Error al generar el reporte');
      }
    } catch (error) {
      console.error('Error al descargar reporte:', error);
      alert('Error al descargar el reporte. Por favor intente nuevamente.');
    } finally {
      setIsDownloading(false);
    }
  };

  // Manejar búsqueda (actualiza query, la búsqueda real se hace con debounce)
  const handleSearchChange = (e) => {
    searchProveedores(e.target.value);
  };

  // Limpiar búsqueda
  const handleClearSearch = () => {
    searchProveedores('');
  };

  // Limpiar errores
  const handleClearError = () => {
    clearError();
  };

  const getThumbnailType = (pais, estado) => {
    if (estado === 'INACTIVE') {
      return 'gray-gradient';
    }
    
    // Colores por país o región
    if (pais && pais.toLowerCase().includes('usa') || pais && pais.toLowerCase().includes('estados unidos')) {
      return 'blue-dark-gradient';
    } else if (pais && (pais.toLowerCase().includes('mexico') || pais.toLowerCase().includes('méxico'))) {
      return 'green-gradient';
    } else if (pais && pais.toLowerCase().includes('china')) {
      return 'red-gradient';
    } else if (pais && pais.toLowerCase().includes('india')) {
      return 'orange-gradient';
    } else {
      return 'purple-gradient';
    }
  };

  const getStatusLabel = (estado) => {
    switch (estado) {
      case 'ACTIVE': return 'Activo';
      case 'INACTIVE': return 'Inactivo';
      case 'PENDING': return 'Pendiente';
      case 'SUSPENDED': return 'Suspendido';
      default: return 'Desconocido';
    }
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando proveedores..." />;
  }

  return (
    <div className="proveedores-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiFileText className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button 
              className="btn btn--ghost btn--sm" 
              onClick={handleClearError}
              style={{ marginLeft: '10px' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}
      
      {/* Título */}
      <h1 className="page-title">PROVEEDORES</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          <button className="menu-item menu-item--first" onClick={handleAddNew}>
            + AGREGAR PROVEEDOR
          </button>
          
          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder="NOMBRE DEL PROVEEDOR"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          
          <button className="menu-item menu-item--download" onClick={handleDownload}>
            DESCARGAR LISTA ↓
          </button>
          
          <div className="menu-item menu-item--views">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
            >
              <FiGrid />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
            >
              <FiList />
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vista de tabla"
            >
              <FiColumns />
            </button>
          </div>
        </div>
      </div>

      {/* Contenido */}
      {proveedores.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiTruck size={48} />
          </div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron proveedores' : 'No hay proveedores registrados'}
          </h3>
          <p className="empty-state-description">
            {searchQuery 
              ? `No se encontraron proveedores que coincidan con "${searchQuery}"`
              : 'Comienza agregando tu primer proveedor al sistema'
            }
          </p>
          {searchQuery && (
            <p>
              <button 
                className="btn btn--ghost" 
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : viewMode === 'table' ? (
        <div className="table-container">
          <table className="data-table data-table--proveedores">
            <thead>
              <tr>
                <th>NOMBRE</th>
                <th>CONTACTO</th>
                <th>PAÍS</th>
                <th>TELÉFONO</th>
                <th className="text-center">PRODUCTOS</th>
                <th className="text-center">ÓRDENES</th>
                <th className="text-center">CRÉDITO</th>
                <th className="text-center">ESTADO</th>
              </tr>
            </thead>
            <tbody>
              {proveedores.map((proveedor) => (
                <tr
                  key={proveedor.id}
                  onClick={() => navigate(`/system/proveedores/${proveedor.id}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <td><strong>{proveedor.nombre}</strong></td>
                  <td>{proveedor.contactoPrincipal || '-'}</td>
                  <td>
                    {proveedor.ciudad ? `${proveedor.ciudad}, ` : ''}{proveedor.pais}
                  </td>
                  <td>{proveedor.telefono || '-'}</td>
                  <td className="text-center">
                    <span className="badge badge-info">
                      {proveedor.productosActivos || 0}
                    </span>
                  </td>
                  <td className="text-center">
                    {proveedor.ordenesCompra || 0}
                  </td>
                  <td className="text-center">
                    {proveedor.diasCredito > 0 ? (
                      <span className="badge badge-success">
                        {proveedor.diasCredito} días
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="text-center">
                    <span className={`badge badge-${proveedor.estado === 'ACTIVE' ? 'success' : 'warning'}`}>
                      {getStatusLabel(proveedor.estado)}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
          {proveedores.map((proveedor) => (
            <div
              key={proveedor.id}
              className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
              onClick={() => navigate(`/system/proveedores/${proveedor.id}`)}
              style={{ cursor: 'pointer' }}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className={`content-thumbnail ${getThumbnailType(proveedor.pais, proveedor.estado)}`}>
                    <span className="logo-text">
                      {proveedor.nombre.split(' ').map(word => word.charAt(0)).join('').substring(0, 4).toUpperCase()}
                    </span>
                    
                    {/* Indicador de estado */}
                    {proveedor.estado !== 'ACTIVE' && (
                      <div className="status-badge status-badge--warning">
                        {getStatusLabel(proveedor.estado)}
                      </div>
                    )}
                  </div>
                  <div className="content-info">
                    <div className="content-title">{proveedor.nombre}</div>
                    <div className="content-subtitle">{proveedor.contactoPrincipal}</div>
                    <div className="content-details">
                      <div className="location-info">
                        <FiMapPin size={12} />
                        <span>{proveedor.ciudad ? `${proveedor.ciudad}, ` : ''}{proveedor.pais}</span>
                      </div>
                      <div className="supplier-stats">
                        <span className="stat-item">
                          {proveedor.productosActivos} productos
                        </span>
                        <span className="stat-item">
                          {proveedor.ordenesCompra} órdenes
                        </span>
                      </div>
                    </div>
                  </div>
                </>
              ) : (
                <>
                  <div className={`content-thumbnail-small ${getThumbnailType(proveedor.pais, proveedor.estado)}`}>
                    <span className="logo-text-small">
                      {proveedor.nombre.split(' ').map(word => word.charAt(0)).join('').substring(0, 3).toUpperCase()}
                    </span>
                  </div>
                  <div className="content-info-list">
                    <div className="content-title">{proveedor.nombre}</div>
                    <div className="content-subtitle">{proveedor.contactoPrincipal}</div>
                    <div className="content-meta">
                      <span className="meta-item">
                        <FiMapPin size={12} /> {proveedor.ciudad ? `${proveedor.ciudad}, ` : ''}{proveedor.pais}
                      </span>
                      <span className="meta-item">Tel: {proveedor.telefono}</span>
                      <span className="meta-item">{proveedor.productosActivos} productos</span>
                      <span className="meta-item">{getStatusLabel(proveedor.estado)}</span>
                      {proveedor.diasCredito > 0 && (
                        <span className="meta-item info">Crédito: {proveedor.diasCredito} días</span>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modal de descarga de reportes */}
      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        title="Descargar Reporte de Proveedores"
        moduleName="proveedores"
        isLoading={isDownloading}
      />
    </div>
  );
};

export default Proveedores;